import { observable, action, runInAction, makeAutoObservable } from "mobx";
import agent from "../api/agent";
import { RootStore } from "./rootStore";
import { toast } from "react-toastify";
import {
  IPayrollFilter,
  getPayrollFilter,
  IPayrollFilterResponse,
  IResendPayrollMailForm,
  IPayrollList,
} from "../models/trackPayroll";
import { IList } from "../models/listOfValours";
import {
  getPayrollDocumentType,
  getProcessResultCode,
} from "../util/listOfValoursOptions";
import { getDateFormatDDMMAAAA } from "../util/dateOptions";
import { ProcessResultCode } from "../util/GeneralEnums";
import { showError } from "../util/errorTypeEnumsOptions";

export default class TrackPayrollStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  @observable employeesAutocomplete: IList[] = [
    { key: `sinFiltro`, text: `Sin Filtro`, value: "" },
  ];
  @observable documentNumberAutocomplete: IList[] = [
    { key: `sinFiltro`, text: `Sin Filtro`, value: "" },
  ];
  @observable btFilter = false;
  @observable payrolls: IPayrollFilterResponse = {
    payrollList: null,
    length: 0,
  };
  @observable paginationSize: number = 5;
  @observable page: number = 1;
  @observable pageSize: number = 30;
  @observable filter: IPayrollFilter = {
    employeeIdentification: "",
    documentID: "",
    payrollType: -1,
    processState: -1,
    issueDateFrom: null,
    issueDateTo: null,
    sortBy: undefined,
    sortDirection: undefined,
  };
  @observable loadData = false;
  @observable acordionFilterState = false;

  @action clearFilter = () => {
    this.filter = {
      employeeIdentification: "",
      documentID: "",
      payrollType: -1,
      processState: -1,
      issueDateFrom: null,
      issueDateTo: null,
      sortBy: undefined,
      sortDirection: undefined,
    };
    this.payrolls = {
      payrollList: null,
      length: 0,
    };
    this.paginationSize = 5;
    this.page = 1;
    this.pageSize = 30;
  };

  @action setDetailReject = (issueDateFrom: Date, issueDateTo: Date) => {
    this.filter = {
      employeeIdentification: "",
      documentID: "",
      payrollType: -1,
      processState: ProcessResultCode.SendSuccessRejected,
      issueDateFrom: issueDateFrom,
      issueDateTo: issueDateTo,
      sortBy: undefined,
      sortDirection: undefined,
    };
  };

  @action searchPayrollById = (id: string) => {
    return this.payrolls.payrollList.find((payroll) => payroll.id === id);
  };

  @action changeAcordionFilterState = () => {
    this.acordionFilterState = !this.acordionFilterState;
  };

  @action changeStatebtFilter = () => {
    this.btFilter = !this.btFilter;
  };

  @action changePageSize = (pageSize: number) => {
    this.pageSize = pageSize;
    this.getTrackPayrolls();
  };

  @action changePage = (page: number) => {
    this.page = page;
    this.getTrackPayrolls();
  };

  @action setFilter = (filter: IPayrollFilter) => {
    this.filter = filter;
    this.changePage(1);
  };

  @action setSorting = (column: string) => {
    if (this.filter.sortBy === column) {
      this.filter.sortDirection = this.filter.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.filter.sortBy = column;
      this.filter.sortDirection = 'asc';
    }
    this.changePage(1);
  };

  @action getEmployeesAutocomplete = async (search: string) => {
    if (search === "") {
      runInAction(() => {
        this.employeesAutocomplete = [
          { key: `sinFiltro`, text: `Sin Filtro`, value: "" },
        ];
      });
    }
    try {
      const check = await agent.TrackPayrolls.getEmployeesAutocomplete(search);
      runInAction(() => {
        this.employeesAutocomplete = [
          { key: `sinFiltro`, text: `Sin Filtro`, value: "" },
        ];
        check.forEach((emp) => {
          this.employeesAutocomplete.push({
            key: `${emp.identification}${emp.name}`,
            text: `${emp.identification}-${emp.name}`,
            value: `${emp.identification}`,
          });
        });
      });
    } catch (error) {}
  };

  @action getDocumentNumberAutocomplete = async (search: string) => {
    if (search === "") {
      runInAction(() => {
        this.documentNumberAutocomplete = [
          { key: `sinFiltro`, text: `Sin Filtro`, value: "" },
        ];
      });
    }

    try {
      const check = await agent.TrackPayrolls.getDocumentNumberAutocomplete(
        search
      );
      runInAction(() => {
        this.documentNumberAutocomplete = [
          { key: `sinFiltro`, text: `Sin Filtro`, value: "" },
        ];
        check.forEach((docNum: any) => {
          this.documentNumberAutocomplete.push({
            key: `${docNum}`,
            text: `${docNum}`,
            value: `${docNum}`,
          });
        });
      });
    } catch (error) {}
  };

  @action getTrackPayrolls = async () => {
    this.changeStatebtFilter();
    try {
      const trackList = await agent.TrackPayrolls.getTrackPayrolls(
        getPayrollFilter(this.filter, this.pageSize, this.page)
      );
      runInAction(() => {
        this.payrolls = trackList;
      });
    } catch (error) {
      showError(error);
    }
    this.changeStatebtFilter();
    runInAction(() => {
      this.loadData = true;
      this.acordionFilterState = false;
    });
  };

  @action resendMail = async (
    values: IResendPayrollMailForm,
    payroll: IPayrollList
  ) => {
    this.rootStore.buttonLoadingStore.changeState();
    try {
      const r = await agent.TrackPayrolls.resendMail(values, payroll);
      if (r.sucess) {
        toast.success("Email enviado correctamente");
        runInAction(() => {
          this.rootStore.modalStore.closeModal();
        });
      } else toast.error("Error:" + r.errorMessage);
    } catch (error) {
      showError(error);
    }
    this.rootStore.buttonLoadingStore.changeState();
  };

  @action resendBulkMail = async (
    values: IResendPayrollMailForm,
    payrollIds: string[],
  ) => {
    this.rootStore.buttonLoadingStore.changeState();
    try {
      const r = await agent.TrackPayrolls.resendBulkMail(values, payrollIds);
      if (r.sucess) {
        toast.success("Emails enviados correctamente");
        runInAction(() => {
          this.rootStore.modalStore.closeModal();
        });
      } else toast.error("Error:" + r.errorMessage);
    } catch (error) {
      showError(error);
    }
    this.rootStore.buttonLoadingStore.changeState();
  };

  @action getFilterInfo = () => {
    let count = 0;
    let info = ``;
    if (
      this.filter.employeeIdentification !== "" &&
      this.filter.employeeIdentification !== undefined
    ) {
      info += `<div class="column filterinfoitem">Empleado: ${this.filter.employeeIdentification}</div>`;
      count++;
    }

    if (
      this.filter.documentID !== "" &&
      this.filter.documentID !== undefined
    ) {
      info += `<div class="column filterinfoitem">No Documento: ${this.filter.documentID}</div>`;
      count++;
    }

    if (count === 3) {
      info += `</div><div class="row filterinforow">`;
      count = 0;
    }

    if (this.filter.processState !== -1) {
      info += `<div class="column filterinfoitem">Estado: ${getProcessResultCode(
        this.filter.processState
      )}</div>`;
      count++;
    }

    if (count === 3) {
      info += `</div><div class="row filterinforow">`;
      count = 0;
    }

    if (this.filter.payrollType !== -1) {
      info += `<div class="column filterinfoitem">Tipo de nómina: ${getPayrollDocumentType(
        this.filter.payrollType
      )}</div>`;
      count++;
    }

    if (count === 3) {
      info += `</div><div class="row filterinforow">`;
      count = 0;
    }

    if ((this.filter.issueDateFrom || this.filter.issueDateTo) !== null) {
      info += `<div class="column filterinfoitem">Fecha emisión: `;
      if (this.filter.issueDateFrom !== null) {
        info += `desde: ${getDateFormatDDMMAAAA(
          this.filter.issueDateFrom.toString()
        )} `;
      }
      if (this.filter.issueDateTo !== null) {
        info += `hasta: ${getDateFormatDDMMAAAA(
          this.filter.issueDateTo.toString()
        )}`;
      }
      info += `</div>`;
      count++;
    }

    if (info !== "") {
      info =
        '<div class="ui three column grid filterinfocontainer"><div class="row filterinforow">' +
        info +
        `</div></div>`;
    }
    return info;
  };
}
